import { ChevronRight, ExternalLink, FileCode } from 'lucide-react';
import { useState } from 'react';

import { Badge } from '@/_shared/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/_shared/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/_shared/components/ui/collapsible';
import { cn } from '@/_shared/lib/utils';

import type { MeasurementPlanChangeType, MeasurementPlanEvent, PropertyChange } from '../types';

const BORDER_CLASS: Record<MeasurementPlanChangeType, string> = {
  add: 'border-l-4 border-l-emerald-500',
  modify: 'border-l-4 border-l-amber-500',
  remove: 'border-l-4 border-l-rose-500',
  already_available: 'border-l-4 border-l-slate-400',
};

const BADGE_VARIANT: Record<MeasurementPlanChangeType, { label: string; className: string }> = {
  add: { label: 'ADD', className: 'bg-emerald-100 text-emerald-700' },
  modify: { label: 'MODIFY', className: 'bg-amber-100 text-amber-700' },
  remove: { label: 'REMOVE', className: 'bg-rose-100 text-rose-700' },
  already_available: { label: 'AVAILABLE', className: 'bg-slate-100 text-slate-700' },
};

const PROP_ROW_TINT: Record<PropertyChange['status'], string> = {
  added: 'bg-emerald-50',
  modified: 'bg-amber-50',
  removed: 'bg-rose-50 line-through',
};

const PROP_MARKER: Record<PropertyChange['status'], string> = {
  added: '+',
  modified: '~',
  removed: '-',
};

/**
 * Returns the URL if it has a safe http(s) scheme, otherwise null.
 * Prevents `javascript:` / `data:` / malformed URLs from becoming clickable links
 * when the agent emits untrusted content into PlanLocation.permalink_url.
 */
function safeHttpUrl(url: string | undefined): string | null {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? url : null;
  } catch {
    return null;
  }
}

interface PlanEventCardProps {
  event: MeasurementPlanEvent;
}

function PropertyRow({ prop }: { prop: PropertyChange }): React.ReactElement {
  return (
    <tr className={cn('border-b border-border/40 last:border-0', PROP_ROW_TINT[prop.status])}>
      <td className="py-2 pr-2 pl-2 font-mono text-[0.6875rem] text-muted-foreground">{PROP_MARKER[prop.status]}</td>
      <td className="py-2 pr-4 font-mono text-[0.8125rem] text-primary">{prop.name}</td>
      <td className="py-2 pr-4 text-[0.8125rem] text-muted-foreground">
        {prop.status === 'modified' && prop.previous_type ? `${prop.previous_type} \u2192 ${prop.type}` : prop.type}
      </td>
      <td className="py-2 pr-4 text-[0.8125rem]">
        {prop.required ? (
          <span className="text-foreground">required</span>
        ) : (
          <span className="text-muted-foreground">optional</span>
        )}
      </td>
      <td className="py-2 text-[0.8125rem] text-muted-foreground">{prop.description}</td>
    </tr>
  );
}

export function PlanEventCard({ event }: PlanEventCardProps): React.ReactElement {
  const [locOpen, setLocOpen] = useState(false);
  const badge = BADGE_VARIANT[event.change_type];
  const properties = event.properties ?? [];
  const locations = event.locations ?? [];
  const showProperties = event.change_type !== 'remove' && properties.length > 0;

  return (
    <Card id={`event-${event.id}`} className={cn('overflow-hidden', BORDER_CLASS[event.change_type])}>
      <CardHeader>
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={cn(
              'inline-flex items-center rounded px-1.5 py-0.5 text-[0.625rem] font-semibold',
              badge.className,
            )}
          >
            {badge.label}
          </span>
          <CardTitle className="font-mono text-base">{event.name}</CardTitle>
          {event.source_ids.map((sid) => (
            <Badge key={sid} variant="outline" className="text-[0.625rem]">
              {sid}
            </Badge>
          ))}
        </div>
        {event.description && <p className="mt-1 text-sm text-muted-foreground">{event.description}</p>}
      </CardHeader>
      <CardContent className="space-y-4">
        {event.change_type === 'already_available' && event.existing_reference && (
          <div className="rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-700">
            <span className="font-semibold">Already available: </span>
            <code className="font-mono">{event.existing_reference}</code>
          </div>
        )}

        {event.when_fired && (
          <div className="rounded-md bg-muted/50 p-3">
            <h4 className="mb-1 text-xs font-medium text-muted-foreground uppercase">When fired</h4>
            <p className="text-[0.875rem]">{event.when_fired}</p>
          </div>
        )}

        {showProperties && (
          <div>
            <h4 className="mb-2 text-sm font-medium text-muted-foreground">Property changes ({properties.length})</h4>
            <div className="overflow-x-auto rounded-md border border-border/60">
              <table className="w-full text-[0.8125rem]">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="py-2 pl-2 font-medium" aria-label="Change marker"></th>
                    <th className="py-2 pr-4 font-medium">Name</th>
                    <th className="py-2 pr-4 font-medium">Type</th>
                    <th className="py-2 pr-4 font-medium">Required</th>
                    <th className="py-2 font-medium">Description</th>
                  </tr>
                </thead>
                <tbody>
                  {properties.map((prop) => (
                    <PropertyRow key={prop.name} prop={prop} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {locations.length > 0 && (
          <Collapsible open={locOpen} onOpenChange={setLocOpen}>
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="flex w-full items-center gap-2 rounded-md bg-muted px-3 py-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/80"
              >
                <ChevronRight size={14} className={cn('transition-transform', locOpen && 'rotate-90')} />
                Code locations ({locations.length})
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <ul className="mt-2 space-y-1 text-xs">
                {locations.map((loc, i) => {
                  const safeHref = safeHttpUrl(loc.permalink_url);
                  const inner = (
                    <>
                      <FileCode size={12} />
                      <span className="font-mono">{loc.repo}</span>
                      <span className="text-muted-foreground/60">/</span>
                      <span className="font-mono">
                        {loc.file}:{loc.line}
                      </span>
                      {safeHref && <ExternalLink size={10} aria-hidden="true" />}
                    </>
                  );
                  return (
                    <li key={`${loc.repo}-${loc.file}-${loc.line}-${i}`}>
                      {safeHref ? (
                        <a
                          href={safeHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground"
                        >
                          {inner}
                        </a>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-muted-foreground">{inner}</span>
                      )}
                      {loc.context && <p className="mt-0.5 ml-5 text-muted-foreground/80">{loc.context}</p>}
                    </li>
                  );
                })}
              </ul>
            </CollapsibleContent>
          </Collapsible>
        )}
      </CardContent>
    </Card>
  );
}
